import { useEffect, useCallback } from 'react'
import { useLocation } from 'react-router-dom'

const defaultScrollOptions = { behavior: 'smooth', block: 'start' }

/**
 * Scrolls to the element whose id matches the current URL hash when the page is ready.
 *
 * @param {Object} [options]
 * @param {boolean} [options.isReady=true] - When true, the scroll will be attempted (e.g. set to !loading when content depends on async data).
 * @param {number} [options.delay=100] - Delay in ms before scrolling, to allow conditional content to render.
 * @param {ScrollIntoViewOptions} [options.scrollOptions] - Options passed to element.scrollIntoView().
 */
export default function useScrollToHash(options = {}) {
  const { hash } = useLocation()
  const {
    isReady = true,
    delay = 100,
    scrollOptions = defaultScrollOptions,
  } = options

  const scrollToElement = useCallback(() => {
    if (!isReady || !hash || hash.length < 2) return
    const id = hash.slice(1)
    const el = document.getElementById(id)
    if (el) {
      el.scrollIntoView(scrollOptions)
    }
  }, [isReady, hash, scrollOptions])

  useEffect(() => {
    if (!isReady || !hash || hash.length < 2) return
    const timeoutId = setTimeout(scrollToElement, delay)
    return () => clearTimeout(timeoutId)
  }, [isReady, hash, delay, scrollToElement])
}
